/**
 * Copyright Mántum All Rights Reserved.
 */
import Internal from '../internal/select';
import ReactSelect from 'react-select/async-creatable';
import debug from 'debug';
import request from '@mantum/kanela-request';
import { KEY_ERROR_STATUS } from '../../../kanela/constant';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { source, helper, accent, validator } from '@mantum/kanela';

import getTranslate from '../../../locales';

import type {
    DataSource,
    Detail,
    Error,
    Filter,
    Group,
    HttpError,
    Locale,
    Option,
    Options,
    Result,
    Status,
    Validation
} from '@mantum/kanela';

const info = debug('kanela:select');
const logger = debug('kanela:qr');

type PropTypes = {
    advanced?: {
        allowAdding?: boolean;
        characters?: number;
        delay?: number;
        entity?: string;
        filter?: Filter;
        group?: Group;
        multiple?: boolean;
        options?: Option<string>;
        result?: Result;
        namespace?: string;
        separator?: string[];
    };
    dataSource?: unknown[] | DataSource;
    disabled?: boolean;
    locale?: Locale;
    error?: boolean;
    instanceId?: number | string;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: Option<unknown> | unknown) => void;
    onClick?: (name?: string, value?: Record<string, unknown>) => void;
    onMessage?: (
        title: string,
        errors?: HttpError | string[] | Error,
        success?: boolean,
        status?: Status
    ) => void;
    optional?: boolean;
    placeholder?: string;
    testid?: string;
    open?: boolean;
    validation?: Validation;
    value?: Option<unknown>;
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function Autocomplete(props, ref) {
        // prettier-ignore
        const {
            advanced = {},
            name,
            disabled = false,
            locale = 'es'
        } = props;

        const {
            characters = 3,
            delay = 500,
            namespace = 'code',
            separator = [/\n/],
            multiple = false
        } = advanced;

        const messageRef = useRef(props.onMessage);
        const timeoutRef = useRef<NodeJS.Timeout>();
        const dataSourceRef = useRef(props.dataSource);
        const clickRef = useRef(props.onClick);
        const separatorRef = useRef(
            new RegExp(separator.toString().replace(/,/g, '|'), 'gi')
        );

        const [loading, setLoading] = useState(false);

        const [original, setOriginal] = useState<unknown[]>([]);
        const [value, setValue] = useState<Options<unknown>>();

        const onLoadOptions = (dataSource?: DataSource) => {
            dataSourceRef.current = dataSource ? dataSource : props.dataSource;
            info(
                '%s: change new datasource %o',
                props.name,
                dataSourceRef.current
            );
        };

        const isValid = useCallback(
            (value: string) => {
                if (value.length < characters) {
                    return false;
                }
                return true;
            },
            [characters]
        );

        const toSelect = useCallback(
            (values: unknown[]): Options<unknown> => {
                const label = props.advanced?.options?.label
                    .split('.')
                    .filter((it) => it !== '') ?? ['label'];

                const value = props.advanced?.options?.value
                    .split('.')
                    .filter((it) => it !== '') ?? ['value'];

                return helper.select.convert(values, label, value);
            },
            [props]
        );

        const getLoadOptions = useCallback(
            (inputValue: string) => {
                return new Promise((resolve, reject) => {
                    const value = inputValue.trim();
                    if (!isValid(value)) {
                        resolve([]);
                        return;
                    }

                    if (timeoutRef.current !== undefined) {
                        clearTimeout(timeoutRef.current);
                    }

                    timeoutRef.current = setTimeout(() => {
                        if (!dataSourceRef.current) {
                            return;
                        }

                        if (!validator.isDataSource(dataSourceRef.current)) {
                            return;
                        }

                        setLoading(true);
                        const dataSource = dataSourceRef.current as DataSource;

                        // prettier-ignore
                        const {
                            parameter = {}
                        } = dataSource

                        const parameters = {
                            ...parameter
                        };

                        Object.keys(parameters).forEach((it) => {
                            const key = parameters[it];
                            if (typeof key === 'string') {
                                parameters[it] = key.replace('{value}', value);
                            }
                        });

                        const url = dataSource.url
                            .replace('{value}', value)
                            .replace('{current}', value);

                        info('search: %s', url);
                        const response = request.fetch(url, {
                            method: dataSource.method,
                            body: parameters,
                            headers: dataSource.headers
                        });

                        response.then((values: unknown[]) => {
                            if (!values) {
                                setLoading(false);
                                return;
                            }

                            const results = source.extract<unknown[]>(
                                values,
                                dataSource.namespace
                            );

                            setLoading(false);
                            if (!Array.isArray(results)) {
                                reject(
                                    new Error('The response value is not array')
                                );
                                return;
                            }

                            setLoading(false);
                            setOriginal(results);

                            const items = toSelect(results);
                            resolve(items);
                        });

                        response.catch((error: unknown) => {
                            setLoading(false);
                            reject(error);
                        });
                    }, delay);
                });
            },
            [isValid, dataSourceRef, delay, toSelect]
        );

        const getLocalLoadOptions = useCallback(
            (inputValue: string) => {
                return new Promise((resolve, reject) => {
                    const value = inputValue.trim();
                    if (!isValid(value)) {
                        resolve([]);
                        return;
                    }

                    if (!Array.isArray(dataSourceRef.current)) {
                        reject(new Error('The data source is not array'));
                        return;
                    }

                    setOriginal(dataSourceRef.current);
                    resolve(
                        toSelect(dataSourceRef.current).filter((row) =>
                            accent(row.label).includes(accent(value))
                        )
                    );
                });
            },
            [dataSourceRef, isValid, toSelect]
        );

        const loadOptions = useCallback(
            (inputValue: string) => {
                return !Array.isArray(dataSourceRef.current)
                    ? getLoadOptions(inputValue)
                    : getLocalLoadOptions(inputValue);
            },
            [getLoadOptions, getLocalLoadOptions, dataSourceRef]
        );

        const getUrl = useCallback((url: string) => {
            try {
                return new URL(url);
            } catch {
                return null;
            }
        }, []);

        const getMessage = useCallback(
            (message: string) => {
                return getTranslate(message, locale);
            },
            [locale]
        );

        useEffect(() => {
            messageRef.current = props.onMessage;
        }, [props.onMessage]);

        useEffect(() => {
            clickRef.current = props.onClick;
        }, [props.onClick]);

        useEffect(() => {
            let text = '';
            const clear = () => {
                text = '';
            };

            const onKeyPress = (event: KeyboardEvent) => {
                if (disabled) {
                    return;
                }

                text = text + event.key;
                if (separatorRef.current.test(text)) {
                    const url = getUrl(text);
                    if (!url && text.length === 0) {
                        if (messageRef.current) {
                            messageRef.current(
                                getMessage(
                                    'An error occurred while reading of the bracelet'
                                ),
                                [
                                    getMessage(
                                        "It's possible that your QR reader isn't configured correctly"
                                    ),
                                    getMessage(
                                        'If the error persists please contact the responsible department'
                                    )
                                ],
                                false,
                                KEY_ERROR_STATUS
                            );
                        }
                        return;
                    }

                    const value = url?.searchParams.get(namespace) ?? text;

                    let currentWord = null;
                    for (const separatorItem of separator) {
                        if (value.includes(`${separatorItem}`)) {
                            currentWord = separatorItem;
                        }
                    }

                    if (value) {
                        const code = currentWord
                            ? value.replace(`${currentWord}`, '')
                            : value;

                        const splitValue = value.split('-');
                        if (splitValue.length === 3) {
                            loadOptions(code)
                                .then((values) => {
                                    setValue(
                                        Array.isArray(values)
                                            ? values
                                            : [values]
                                    );

                                    if (clickRef.current) {
                                        clickRef.current(name, {
                                            [namespace]: values
                                        });
                                    }
                                })
                                .catch(({ message, errors }: HttpError) => {
                                    const error =
                                        errors &&
                                        'individuo' in errors &&
                                        errors?.individuo
                                            ? errors.individuo
                                            : getMessage('Not carcass found');

                                    if (messageRef.current) {
                                        messageRef.current(
                                            getMessage(message),
                                            [`${error}`],
                                            false,
                                            KEY_ERROR_STATUS
                                        );
                                    }
                                    logger('An error ocurred', message);
                                });
                        } else {
                            if (clickRef.current) {
                                clickRef.current(name, {
                                    [namespace]: code
                                });
                            }
                        }
                    }

                    clear();
                }
            };

            document.addEventListener('keypress', onKeyPress);
            return () => {
                clear();
                document.removeEventListener('keypress', onKeyPress);
            };
        }, [
            getUrl,
            getMessage,
            loadOptions,
            name,
            namespace,
            disabled,
            separator
        ]);

        return (
            <Internal
                {...props}
                cacheOptions={false}
                dataSource={[]}
                onMessage={props.onMessage}
                loadOptions={loadOptions}
                loading={loading}
                multiple={multiple}
                onLoadOptions={onLoadOptions}
                original={original}
                ref={ref}
                value={value ?? props.value}
                render={ReactSelect}
            />
        );
    }
);
